import axios, { AxiosError, CanceledError, InternalAxiosRequestConfig } from 'axios';

import type { ITokensResponse } from './auth';
import {
  getAuthSessionRevision,
  isAuthSessionCurrent,
  replaceAuthTokens,
} from './auth/session';
import {
  AUTH_SESSION_EXPIRED_EVENT,
  APP_DATA_MUTATED_EVENT,
  ORGANIZATION_FROZEN_EVENT,
  FORBIDDEN_ACCESS_EVENT,
} from './events';

declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * For optional lookups (e.g. decorating a tooltip): a 403 is handled by the
     * caller and must not replace the page with the access-denied screen.
     */
    suppressForbiddenEvent?: boolean;
  }
}

interface IRetryRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _authSession?: number;
}

const UNAUTHORIZED_STATUS = 401;
const FORBIDDEN_STATUS = 403;
const LOCKED_STATUS = 423;
const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
});

const axiosApiInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
});

interface IRefreshRequest {
  owner: number;
  tokens: ITokensResponse;
  promise: Promise<ITokensResponse>;
}
let refreshRequest: IRefreshRequest | null = null;

const getStoredTokens = (): ITokensResponse | null => {
  const accessToken = localStorage.getItem('accessToken');
  const refreshToken = localStorage.getItem('refreshToken');

  return accessToken && refreshToken ? { accessToken, refreshToken } : null;
};

const setAuthorizationHeader = (
  config: InternalAxiosRequestConfig,
  accessToken: string,
): void => {
  if (typeof config.headers.set === 'function') {
    config.headers.set('Authorization', `Bearer ${accessToken}`);
    return;
  }

  config.headers.Authorization = `Bearer ${accessToken}`;
};

// GET responses (e.g. account/me) can otherwise be served from the browser's
// HTTP cache after a normal reload, so a user approved into an organization
// keeps seeing their pre-approval access until a hard reload bypasses it.
const disableGetCaching = (config: InternalAxiosRequestConfig): void => {
  if ((config.method || 'get').toLowerCase() !== 'get') {
    return;
  }

  config.params = { ...config.params, _: Date.now() };

  if (typeof config.headers.set === 'function') {
    config.headers.set('Cache-Control', 'no-cache');
    return;
  }

  config.headers['Cache-Control'] = 'no-cache';
};

const expireSession = (owner: number, tokens: ITokensResponse): void => {
  if (
    !isAuthSessionCurrent(owner) ||
    localStorage.getItem('accessToken') !== tokens.accessToken ||
    localStorage.getItem('refreshToken') !== tokens.refreshToken
  ) {
    return;
  }

  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  window.dispatchEvent(new Event(AUTH_SESSION_EXPIRED_EVENT));
};

const refreshAccessToken = (): Promise<ITokensResponse> => {
  const tokens = getStoredTokens();
  if (!tokens) {
    return Promise.reject(new Error('Authentication tokens are missing.'));
  }

  const owner = getAuthSessionRevision();
  if (
    refreshRequest?.owner === owner &&
    refreshRequest.tokens.accessToken === tokens.accessToken &&
    refreshRequest.tokens.refreshToken === tokens.refreshToken
  ) {
    return refreshRequest.promise;
  }
  const promise = axiosInstance
    .post<ITokensResponse>('/security/refresh', tokens)
    .then(response => {
      if (!replaceAuthTokens(owner, tokens, response.data)) {
        throw new CanceledError('Authentication session changed during token refresh.');
      }

      return response.data;
    })
    .catch(error => {
      expireSession(owner, tokens);
      return Promise.reject(error);
    })
    .finally(() => {
      if (refreshRequest?.promise === promise) refreshRequest = null;
    });

  refreshRequest = { owner, tokens, promise };
  return promise;
};

const ownsRequest = (config: IRetryRequestConfig): boolean =>
  config._authSession !== undefined &&
  isAuthSessionCurrent(config._authSession) &&
  !config.signal?.aborted;

axiosApiInstance.interceptors.request.use(
  (config: IRetryRequestConfig) => {
    if (config._authSession === undefined) config._authSession = getAuthSessionRevision();
    if (!ownsRequest(config)) throw new CanceledError('Request is no longer current.');
    const accessToken = localStorage.getItem('accessToken');

    if (accessToken) {
      setAuthorizationHeader(config, accessToken);
    }

    disableGetCaching(config);

    return config;
  },
  error => {
    throw error;
  },
  { synchronous: true },
);

axiosApiInstance.interceptors.response.use(
  response => {
    if (!ownsRequest(response.config as IRetryRequestConfig)) {
      throw new CanceledError('Response is no longer current.');
    }
    // Announce successful writes so cached views (e.g. dashboard) know their
    // data may be stale and can refetch on next visit.
    const method = response.config.method?.toLowerCase();
    if (method && MUTATING_METHODS.has(method)) {
      window.dispatchEvent(new Event(APP_DATA_MUTATED_EVENT));
    }
    return response;
  },
  async (error: AxiosError): Promise<unknown> => {
    const originalRequest = error.config as IRetryRequestConfig | undefined;
    if (!originalRequest || !ownsRequest(originalRequest)) {
      return Promise.reject(new CanceledError('Request is no longer current.'));
    }

    const frozenResponse = error.response?.data as
      | { code?: string; message?: string; gracePeriodEndsAt?: string | null }
      | undefined;
    const forbiddenResponse = error.response?.data as { message?: string } | undefined;

    if (
      error.response?.status === LOCKED_STATUS &&
      frozenResponse?.code === 'ORGANIZATION_FROZEN'
    ) {
      window.dispatchEvent(
        new CustomEvent(ORGANIZATION_FROZEN_EVENT, {
          detail: {
            code: frozenResponse.code,
            message: frozenResponse.message || 'Organization access is frozen.',
            gracePeriodEndsAt: frozenResponse.gracePeriodEndsAt || null,
          },
        }),
      );
    }

    if (
      error.response?.status === FORBIDDEN_STATUS &&
      !originalRequest?.suppressForbiddenEvent
    ) {
      window.dispatchEvent(
        new CustomEvent(FORBIDDEN_ACCESS_EVENT, {
          detail: {
            message: forbiddenResponse?.message || 'You do not have access to this data.',
          },
        }),
      );
    }

    if (
      error.response?.status !== UNAUTHORIZED_STATUS ||
      !originalRequest ||
      originalRequest._retry ||
      !getStoredTokens()
    ) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      const tokens = await refreshAccessToken();
      if (!ownsRequest(originalRequest)) {
        throw new CanceledError('Request is no longer current.');
      }
      setAuthorizationHeader(originalRequest, tokens.accessToken);
      return axiosApiInstance.request(originalRequest);
    } catch (refreshError) {
      return Promise.reject(refreshError);
    }
  },
);

export {
  APP_DATA_MUTATED_EVENT,
  AUTH_SESSION_EXPIRED_EVENT,
  FORBIDDEN_ACCESS_EVENT,
  ORGANIZATION_FROZEN_EVENT,
  axiosInstance,
  axiosApiInstance,
  refreshAccessToken,
};
